import { useRouter } from 'next/router';
import { useEffect } from 'react';

import { ChainAddress } from '@/utils/models';
import { isArcUsdcPair } from '@/utils/multichain';
import { normalizeTokenAddress } from '@/utils/normalizeAddress';
import { getDefaultPairForChain, getDefaultTradeParams } from '@/utils/swap';

type SyncUrlParameters = (
  buyAddress?: ChainAddress,
  buyChain?: number,
  sellAddress?: ChainAddress,
  sellChain?: number,
) => void;

type UseInitializeDefaultTokensParams = {
  buyTokenAddress: ChainAddress | undefined;
  buyTokenChainId: number | undefined;
  sellTokenAddress: ChainAddress | undefined;
  sellTokenChainId: number | undefined;
  /**
   * Writes the resolved token query parameters to the URL. Each consumer
   * decides how to render them (e.g. token pages ignore the buy side because it
   * is the page token).
   */
  syncUrlParameters: SyncUrlParameters;
};

/** Chain-aware address equality (EVM is case-insensitive, Solana is not). */
const addressesEqual = (
  a: ChainAddress | undefined,
  b: ChainAddress | undefined,
  chainId: number,
): boolean =>
  a != null && b != null && normalizeTokenAddress(a, chainId) === normalizeTokenAddress(b, chainId);

/**
 * Ensures the trade module never renders with an empty token field by filling
 * the empty side(s) with the static default pair (see ADR 0001). The selection
 * is independent of the connected wallet and its balances.
 *
 * - Both sides empty: fill with the fixed Ethereum USDC -> ETH pair.
 * - Exactly one side empty (token pages, deep links): fill the empty side on
 *   the present side's chain, flipping to the counterpart's default on
 *   collision (e.g. a USDC page becomes native -> USDC).
 * - Both sides present: leave the pair alone.
 */
export const useInitializeDefaultTokens = ({
  buyTokenAddress,
  buyTokenChainId,
  sellTokenAddress,
  sellTokenChainId,
  syncUrlParameters,
}: UseInitializeDefaultTokensParams) => {
  const router = useRouter();

  useEffect(() => {
    if (!router.isReady) return;

    const buyEmpty = buyTokenAddress == null;
    const sellEmpty = sellTokenAddress == null;

    if (!buyEmpty && !sellEmpty) return;

    if (buyEmpty && sellEmpty) {
      const defaults = getDefaultTradeParams();
      syncUrlParameters(
        defaults.buyAddress,
        Number(defaults.buyChain),
        defaults.sellAddress,
        Number(defaults.sellChain),
      );
      return;
    }

    // The present side always has its chainId set alongside its address; bail
    // rather than resolving defaults against an undefined chain.
    const presentChainId = buyEmpty ? sellTokenChainId : buyTokenChainId;
    if (presentChainId == null) return;

    const { buyAddress, sellAddress } = getDefaultPairForChain(presentChainId);

    if (buyEmpty) {
      // Flip to the sell default if the native buy would collide with the sell.
      const nextBuy = addressesEqual(sellTokenAddress, buyAddress, presentChainId)
        ? sellAddress
        : buyAddress;
      syncUrlParameters(nextBuy, presentChainId, sellTokenAddress, sellTokenChainId);
    } else {
      // Flip to the buy default if the USDC sell would collide with the buy.
      const nextSell =
        addressesEqual(buyTokenAddress, sellAddress, presentChainId) ||
        isArcUsdcPair(buyTokenAddress, sellAddress, presentChainId)
          ? buyAddress
          : sellAddress;
      syncUrlParameters(buyTokenAddress, buyTokenChainId, nextSell, presentChainId);
    }
  }, [
    router.isReady,
    buyTokenAddress,
    buyTokenChainId,
    sellTokenAddress,
    sellTokenChainId,
    syncUrlParameters,
  ]);
};
